import View from "constants/view";
import { IRouteHierarchy, PageNames } from "types-for/routes";

const RoutesHierarchy: IRouteHierarchy = {
    Root: {
        children: {
            [PageNames.Login]: {
                path: "/",
                lazy: () => import("components/login/login"),
            },
            [PageNames.CallbackOAuth]: {
                path: View.CallbackOAuth,
                lazy: () => import("components/callbacks/oauth/oauth-callback"),
            },
            [PageNames.Authenticate]: {
                path: "/authenticate",
                lazy: () => import("components/authenticate/authenticate"),
            },
            [PageNames.AuthenticatedRedirect]: {
                path: "/authenticated-redirect",
                lazy: () => import("components-for/authentication/authenticated-redirect"),
            },
            [PageNames.Main]: {
                path: "/main",
                lazy: () => import("components/application/authenticated-routes/authenticated-routes"),
                children: {
                    MainRoot: {
                        path: "main",
                    },
                    [PageNames.AgentHome]: {
                        path: "home",
                        lazy: () => import("components/agent-home/agent-home"),
                    },
                    [PageNames.Chat]: {
                        path: "chat",
                        lazy: () => import("components/chat/chat"),
                    },
                    [PageNames.CreateEmail]: {
                        path: "new-email",
                        lazy: () => import("components/create-email/create-email"),
                    },
                    [PageNames.DeferredQueries]: {
                        path: "deferred-queries",
                        lazy: () => import("components/deferred-queries/deferred-queries"),
                    },
                    [PageNames.Email]: {
                        path: "query",
                        lazy: () => import("components/query/query"),
                    },
                    [PageNames.LogicFlows]: {
                        path: "flows",
                        lazy: () => import("components/logic-flows/logic-flows"),
                    },
                    [PageNames.LogicFlow]: {
                        path: "flow",
                        lazy: () => import("components/logic-flow/logic-flow"),
                    },
                    [PageNames.Templates]: {
                        path: "templates",
                        lazy: () => import("components/templates/templates"),
                    },
                    [PageNames.ProactiveChat]: {
                        path: "proactive-chat",
                        lazy: () => import("components/proactive-chat/proactive-chat"),
                    },
                    [PageNames.QueryHistory]: {
                        path: "query-history",
                        lazy: () => import("components/query-history-thread-page/query-history-thread-page"),
                    },
                    [PageNames.Permissions]: {
                        path: "permissions",
                        lazy: () => import("components/permissions"),
                    },
                    [PageNames.Users]: {
                        path: "users",
                        lazy: () => import("components/users/users"),
                    },
                    [PageNames.Reporting]: {
                        path: "reporting",
                        lazy: () => import("components/reporting/reporting"),
                        children: {
                            [PageNames.ReportingChat]: {
                                path: "chat",
                                lazy: () => import("components/reporting-chat"),
                            },
                            [PageNames.ReportingQueue]: {
                                path: "queue",
                                lazy: () => import("components/reporting-queue/reporting-queue"),
                            },
                            [PageNames.ReportingHistoric]: {
                                path: "historic",
                                lazy: () => import("components/reporting/reporting-historic"),
                            },
                            [PageNames.ReportingReports]: {
                                path: "reports",
                                lazy: () => import("components/reporting/reporting-reports/"),
                                children: {
                                    [PageNames.ReportingReportsNew]: {
                                        path: "new",
                                        lazy: () => import("components/reporting/reporting-reports/pages/new-report"),
                                    },
                                    [PageNames.ReportingReportsEdit]: {
                                        path: ":reportId/edit",
                                        lazy: () => import("components/reporting/reporting-reports/pages/edit-report"),
                                    },
                                    [PageNames.ReportingReportsCopy]: {
                                        path: ":reportId/copy",
                                        lazy: () => import("components/reporting/reporting-reports/pages/copy-report"),
                                    },
                                    [PageNames.ReportingReportsDisplay]: {
                                        path: ":reportId",
                                        lazy: () =>
                                            import("components/reporting/reporting-reports/pages/display-report"),
                                    },
                                },
                            },
                            [PageNames.ReportingPublicEmail]: {
                                path: "public-email",
                                lazy: () => import("components/reporting/reporting-public-email"),
                            },
                            [PageNames.ReportingPublicChat]: {
                                path: "public-chat",
                                lazy: () => import("components/reporting/reporting-public-chat"),
                            },
                            [PageNames.CallWallboard]: {
                                path: "call-wallboard",
                                lazy: () => import("components/reporting/call-wallboard"),
                            },
                            [PageNames.ReportingAgents]: {
                                path: "agents",
                                lazy: () => import("components/reporting/live-agents"),
                            },
                        },
                    },
                    [PageNames.Search]: {
                        path: "search",
                        lazy: () => import("components/search/search"),
                        children: {
                            [PageNames.SearchInteractions]: {
                                path: "interactions",
                                lazy: () => import("components/search-emails/search-emails"),
                            },
                            [PageNames.SearchQueries]: {
                                path: "queries",
                                lazy: () => import("components/search-emails/search-emails"),
                            },
                        },
                    },
                    [PageNames.Settings]: {
                        path: "settings",
                        lazy: () => import("components/settings/settings"),
                        children: {
                            [PageNames.SettingsBans]: {
                                path: "bans",
                                lazy: () => import("components/settings/bans"),
                            },
                            [PageNames.SettingsBrands]: {
                                path: "brands",
                                lazy: () => import("components/settings/brands"),
                            },
                            [PageNames.SettingsChatWidgetAppearances]: {
                                path: "chat-widget-appearances",
                                lazy: () => import("components/settings/chat-widget-appearances"),
                            },
                            [PageNames.SettingsForms]: {
                                path: "forms",
                                lazy: () => import("components/settings/forms"),
                                children: {
                                    [PageNames.SettingsFormsEdit]: {
                                        path: ":formId",
                                        lazy: () => import("components/settings/forms"),
                                    },
                                },
                            },
                            [PageNames.SettingsSetups]: {
                                path: "setups",
                                lazy: () => import("components/settings/setups"),
                            },
                            [PageNames.SettingsLanguages]: {
                                path: "languages",
                                lazy: () => import("components/settings/languages"),
                            },
                            [PageNames.SettingsSystem]: {
                                path: "system",
                                lazy: () => import("components/settings/system"),
                            },
                            [PageNames.SettingsThemes]: {
                                path: "themes",
                                lazy: () => import("components/settings/themes"),
                            },
                            [PageNames.SettingsEmailRoutingRules]: {
                                path: "email-routing-rules",
                                lazy: () => import("components/settings/email-routing-rules"),
                            },
                            [PageNames.SettingsEmailAccounts]: {
                                path: "email-accounts",
                                lazy: () => import("components/settings/email-accounts"),
                            },
                            [PageNames.SettingsDepartments]: {
                                path: "departments",
                                lazy: () => import("components/settings/departments"),
                            },
                            [PageNames.SettingsSkills]: {
                                path: "skills",
                                lazy: () => import("components/settings/skills"),
                            },
                            [PageNames.SettingsUserStates]: {
                                path: "user-states",
                                lazy: () => import("components/settings/user-states"),
                            },
                            [PageNames.SettingsTextLocalisations]: {
                                path: "text-localisations",
                                lazy: () => import("components/settings/text-localisations"),
                                children: {
                                    [PageNames.SettingsTextLocalisationsEdit]: {
                                        path: ":languageId",
                                        lazy: () => import("components/settings/text-localisations"),
                                    },
                                },
                            },
                        },
                    },
                },
            },
            [PageNames.NoMatch]: {
                path: "*",
                lazy: () => import("@edgetier/client-components").then((module) => ({ default: module.NoMatch })), // TODO: make a proper no match page.
            },
        },
    },
};

/**
 * Function to get the absolute path of a given PageName
 * @param pageName      The page name of the route we want the absolute path for.
 * @param hierarchy     The routes hierarchy to search in. Defaults to the app's routes hierarchy.
 * @returns             A function that can be called to get the absolute path of a given PageName. If the provided route has parameters, just send them to the function returned by this function.
 */
const getAbsolutePath = (pageName: PageNames, hierarchy: IRouteHierarchy = RoutesHierarchy): string | null => {
    const pageConfig = hierarchy[pageName];
    if (pageConfig) {
        return pageConfig.path || "/";
    } else {
        for (const key in hierarchy) {
            if (hierarchy[key].children) {
                const nestedPath = getAbsolutePath(pageName, hierarchy[key].children);
                if (nestedPath) {
                    return (hierarchy[key].path || "") + (nestedPath[0] === "/" ? nestedPath : `/${nestedPath}`);
                }
            }
        }
    }
    return null;
};

const AbsolutePaths: { [pageName in PageNames]: (...args: Array<string>) => string } = Object.keys(PageNames).reduce(
    (acc, pageName) => {
        const pageNameKey = pageName as PageNames;
        const path = getAbsolutePath(pageNameKey, RoutesHierarchy)?.replace("//", "/");
        if (!path) {
            throw new Error(`No path found for page name ${pageNameKey}`);
        }
        if (pageNameKey !== PageNames.NoMatch) {
            acc[pageNameKey] = (...args: Array<string>) => {
                let localPath = path;
                const paramMatches = localPath.match(/:\w+\??/g);
                if (paramMatches && paramMatches.length === args.length) {
                    paramMatches.forEach((param, index) => {
                        localPath = localPath.replace(param, args[index] || "");
                    });
                }
                return localPath;
            };
        }
        return acc;
    },
    {} as { [pageName in PageNames]: (...args: Array<string>) => string }
);

/**
 * Retrieve the relative path to a specific page from a given origin page.
 * @param currentPageName PageName of the origin page.
 * @param destinationPageName PageName of the destination page.
 * @param hierarchy The routes hierarchy to search in. Defaults to the app's routes hierarchy.
 * @returns The relative path to the destination page from the origin page.
 */
const getRelativePath = (
    currentPageName: PageNames,
    destinationPageName: PageNames,
    hierarchy?: IRouteHierarchy
): string | null => {
    const currentAbsPath = getAbsolutePath(currentPageName, hierarchy);
    const destinationAbsPath = getAbsolutePath(destinationPageName, hierarchy);

    if (!currentAbsPath || !destinationAbsPath) {
        return null;
    }

    const currentParts = currentAbsPath.split("/");
    const destinationParts = destinationAbsPath.split("/");

    let i = 0;
    while (i < currentParts.length && i < destinationParts.length && currentParts[i] === destinationParts[i]) {
        i++;
    }

    const backPaths = new Array(currentParts.length - i).fill("..");
    const forwardPaths = destinationParts.slice(i);

    return [...backPaths, ...forwardPaths].join("/");
};

export { AbsolutePaths, getRelativePath };

export default RoutesHierarchy;
